import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LiveTicker from '@/components/LiveTicker';
import MarketplaceFeed from '@/components/MarketplaceFeed';
import OffersBanner from '@/components/OffersBanner';
import { ShieldCheck, TrendingUp, Coins, Zap, ArrowRight, Wallet as WalletIcon, Rocket } from 'lucide-react';

export default function Landing() {
  return (
    <div className="bg-[#0B0E11] text-[#EAECEF] min-h-screen">
      <LiveTicker />
      <MarketplaceFeed />
      <OffersBanner />
      {/* Hero */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <img src="/bitgonode-logo.png" alt="BITGONODE" className="h-24 sm:h-32 w-auto object-contain mb-6" data-testid="hero-logo" />
          <div className="inline-flex items-center gap-2 rounded-full bg-[#181A20] border border-[#2B3139] px-3 py-1 text-xs text-[#FCD535] mb-6">
            <Zap className="w-3.5 h-3.5" /> The Gateway to Digital Wealth — 100+ coins, 0% deposit fees
          </div>
          <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] mb-6">
            The world&apos;s fastest<br/>
            <span className="text-[#FCD535]">crypto exchange</span>
          </h1>
          <p className="text-[#B7BDC6] text-lg max-w-xl mb-8 leading-relaxed">
            Buy, sell, and stake cryptocurrencies with institutional-grade security.
            Get $10,000 in demo USDT the moment you sign up.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/signup">
              <Button size="lg" className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-full px-7 h-12" data-testid="hero-signup-btn">
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/markets">
              <Button size="lg" variant="outline" className="border-[#2B3139] bg-transparent text-white hover:bg-[#181A20] hover:text-white rounded-full px-7 h-12" data-testid="hero-markets-btn">
                View Markets
              </Button>
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            {[
              ['$18B+', '24h Volume'],
              ['100+', 'Coins Listed'],
              ['12M+', 'Traders'],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-mono text-2xl sm:text-3xl text-white font-medium tracking-tight">{n}</div>
                <div className="text-xs uppercase tracking-widest text-[#848E9C] mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="absolute inset-0 bg-[#FCD535]/10 blur-3xl rounded-full" />
          <img src="https://images.unsplash.com/photo-1643858131883-c96a58cb7e15?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHwzZCUyMGNyeXB0byUyMGdvbGQlMjBjb2luJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgxOTQzNjY1fDA&ixlib=rb-4.1.0&q=85" alt="Gold crypto coin" className="relative w-full max-w-[520px] mx-auto object-contain" />
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 py-20">
        <h2 className="font-display text-white text-3xl sm:text-4xl font-semibold tracking-tight mb-10">Everything you need to trade</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: TrendingUp, title: 'Spot Trading', desc: 'Real-time order book, candlestick charts, and instant execution.' },
            { icon: Coins, title: 'Staking', desc: 'Earn up to 120% APY with flexible and fixed-term pools.' },
            { icon: ShieldCheck, title: 'Secure KYC', desc: 'Bank-grade verification to unlock higher limits and withdrawals.' },
            { icon: WalletIcon, title: 'Demo Wallet', desc: '$10,000 USDT to practice trading risk-free from day one.' },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-[#181A20] border border-[#2B3139] p-6 hover:border-[#FCD535]/40 transition-colors">
              <Icon className="w-8 h-8 text-[#FCD535] mb-4" strokeWidth={1.5} />
              <div className="font-display text-white text-xl font-semibold mb-2">{title}</div>
              <div className="text-[#B7BDC6] text-sm leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Staking teaser */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 py-20">
        <div className="bg-gradient-to-br from-[#181A20] to-[#0B0E11] border border-[#2B3139] p-10 sm:p-16 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#FCD535]/10 blur-3xl rounded-full" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#FCD535] mb-4">Passive Income</div>
              <h3 className="font-display text-white text-4xl sm:text-5xl font-bold tracking-tighter mb-4">
                Stake your crypto,<br/>earn up to <span className="text-[#FCD535]">120% APY</span>
              </h3>
              <p className="text-[#B7BDC6] text-base mb-6 max-w-md">
                Choose from flexible daily rewards or lock-in higher yields with fixed-term pools.
              </p>
              <Link to="/staking">
                <Button className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-full px-6" data-testid="cta-stake">
                  Start Earning <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { sym: 'BTC', apy: '8.5%', d: '30D' },
                { sym: 'ETH', apy: '12%', d: '60D' },
                { sym: 'SOL', apy: '32%', d: '120D' },
                { sym: 'DOGE', apy: '120%', d: '365D' },
              ].map(p => (
                <div key={p.sym} className="bg-[#0B0E11] border border-[#2B3139] p-5">
                  <div className="text-[#848E9C] text-xs uppercase tracking-wider">{p.sym} · {p.d} Lock</div>
                  <div className="font-mono text-[#FCD535] text-3xl font-medium tracking-tight mt-2">{p.apy}</div>
                  <div className="text-xs text-[#848E9C] mt-1">APY</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* List your token CTA — accessible without login */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 py-12" data-testid="landing-list-token-cta">
        <div className="relative overflow-hidden border border-[#FCD535]/40 bg-gradient-to-br from-[#181A20] via-[#1E2026] to-[#0B0E11] p-8 sm:p-10">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#FCD535] to-transparent" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-[#FCD535]/10 blur-3xl pointer-events-none" />
          <div className="relative grid md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase font-mono tracking-widest text-[#FCD535] mb-3">
                <Rocket className="w-3 h-3" /> For Builders & Founders
              </div>
              <h2 className="font-display text-white text-3xl sm:text-4xl font-bold tracking-tighter">
                Have a token? <span className="text-[#FCD535]">List it on BITGONODE.</span>
              </h2>
              <p className="text-[#B7BDC6] text-sm sm:text-base mt-3 max-w-2xl leading-relaxed">
                Reach millions of active traders. Apply for spot trading or ignite your project via our Launchpad — transparent process, dedicated growth support, and case-by-case listing fees.
              </p>
            </div>
            <div className="md:justify-self-end">
              <Link to="/list-your-coin">
                <Button className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm h-12 px-8 text-base" data-testid="landing-list-token-btn">
                  List your token <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <div className="text-[10px] text-[#848E9C] mt-2 text-right">No login required to view details</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#2B3139] py-10 mt-10">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#848E9C]">
          <div>© 2026 BITGONODE. Demo platform — no real funds involved.</div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
